import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import ClassForm from './ClassForm';
import Notification from '../common/Notification';
import { fetchClasses, addClass, updateClass, deleteClass, fetchStudentCountsByClass } from '../../utils/supabaseClient';
import '../../styles/Classes.css';


// Format column name for display
const formatColumnName = (columnName) => {
  if (!columnName) return '';
  // Convert snake_case to Title Case with spaces
  return columnName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

function Classes() {

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('class_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchColumn, setSearchColumn] = useState('all');
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [columns, setColumns] = useState([]);
  const [showClassForm, setShowClassForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [studentCounts, setStudentCounts] = useState({});

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  // Fetch classes from Supabase
  const fetchClassesData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const classesData = await fetchClasses();
      
      // Fetch student counts per class
      let counts = {};
      try {
        counts = await fetchStudentCountsByClass();
        setStudentCounts(counts);
      } catch (countErr) {
        console.error('Error loading student counts:', countErr);
        setStudentCounts({});
      }
      
      setClasses(classesData);
      setFilteredClasses(classesData);
      
      // Extract columns from the first class record if available
      if (classesData.length > 0) {
        const firstClass = classesData[0];
        const excludedFields = ['id', 'created_at', 'updated_at', 'class_teacher_name', 'teacher', 'staff'];
        const columnKeys = Object.keys(firstClass).filter(
          key => !excludedFields.includes(key)
        );
        setColumns(columnKeys);
      } else {
        // Set default columns if no data
        setColumns(['class_name', 'class_teacher', 'academic_year', 'max_students', 'is_active']);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to load classes. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchClassesData();
  }, []);

  // Filter classes when search term or column changes
  useEffect(() => {
    const filtered = classes.filter(cls => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      
      if (searchColumn === 'all') {
        return Object.entries(cls).some(([key, val]) => 
          !['id', 'created_at', 'updated_at'].includes(key) && 
          String(val).toLowerCase().includes(searchLower)
        );
      }
      
      const columnValue = cls[searchColumn];
      return columnValue ? String(columnValue).toLowerCase().includes(searchLower) : false;
    });
    
    setFilteredClasses(filtered);
    setCurrentPage(1);
  }, [searchTerm, searchColumn, classes]);

  // Sort the filtered classes
  const sortedClasses = useMemo(() => {
    return [...filteredClasses].sort((a, b) => {
      // Sort by active student count if sorting by 'count'
      if (sortField === 'count') {
        const aCount = studentCounts[a.class_name]?.active || 0;
        const bCount = studentCounts[b.class_name]?.active || 0;
        return sortDirection === 'asc' ? aCount - bCount : bCount - aCount;
      }
      // Handle null or undefined values
      if (!a[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (!b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      
      // Sort strings case-insensitive
      if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
        return sortDirection === 'asc'
          ? a[sortField].localeCompare(b[sortField])
          : b[sortField].localeCompare(a[sortField]);
      }
      
      // Sort numbers
      if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
        return sortDirection === 'asc' 
          ? a[sortField] - b[sortField] 
          : b[sortField] - a[sortField];
      }
      
      // Sort booleans
      if (typeof a[sortField] === 'boolean' && typeof b[sortField] === 'boolean') {
        return sortDirection === 'asc' 
          ? (a[sortField] === b[sortField] ? 0 : a[sortField] ? -1 : 1)
          : (a[sortField] === b[sortField] ? 0 : a[sortField] ? 1 : -1);
      }
      
      return 0;
    });
  }, [filteredClasses, sortField, sortDirection, studentCounts]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedClasses.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedClasses.length / recordsPerPage);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Generate column options for search
  const columnOptions = useMemo(() => {
    const options = columns.map(col => ({
      value: col,
      label: formatColumnName(col)
    }));
    
    return [
      { value: 'all', label: 'All Columns' },
      ...options
    ];
  }, [columns]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle export to Excel
  const handleExport = (allRecords = true) => {
    try {
      let dataToExport = allRecords ? [...filteredClasses] : currentRecords;

      // Format data for export
      const exportData = dataToExport.map(cls => {
        const exportItem = {};
        columns.forEach(col => {
          exportItem[formatColumnName(col)] = formatCellValue(cls[col], col, cls);
        });
        return exportItem;
      });

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Auto-size columns
      if (exportData.length > 0) {
        const columnWidths = Object.keys(exportData[0]).map(key => ({
          wch: Math.max(
            key.length, // header length
            ...exportData.map(row => String(row[key] || '').length) // max content length
          )
        }));
        ws['!cols'] = columnWidths;
      }
      
      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Classes');
      
      // Generate the Excel file and trigger download
      const fileName = `classes_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showNotification('Export completed successfully!', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showNotification('Failed to export data. Please try again.', 'error');
    }
  };

  // Close all modals
  const closeAllModals = () => {
    setShowClassForm(false);
    setShowDeleteConfirm(false);
    setEditingClass(null);
    setClassToDelete(null);
  };

  // Handle edit class
  const handleEdit = (cls) => {
    closeAllModals();
    setEditingClass(cls);
    setShowClassForm(true);
  };

  // Handle add class
  const handleAddClass = async (newClass) => {
    setIsSaving(true);
    try {
      // Remove virtual fields that don't exist in the database
      const { class_teacher_name, teacher, staff, ...cleanData } = newClass;
      
      const addedClass = await addClass(cleanData);
      
      // Update both classes and filteredClasses to keep them in sync
      setClasses(prevClasses => [addedClass, ...prevClasses]);
      setFilteredClasses(prev => [addedClass, ...prev]);
      
      closeAllModals();
      showNotification('Class added successfully!', 'success');
    } catch (error) {
      console.error('Error adding class:', error);
      showNotification(`Failed to add class: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle update class
  const handleUpdateClass = async (updatedData) => {
    if (!editingClass) return;
    
    setIsSaving(true);
    try {
      // Remove virtual fields that don't exist in the database
      const { class_teacher_name, teacher, staff, ...cleanData } = updatedData;
      
      const updatedClass = await updateClass(editingClass.id, cleanData);
      
      // Update local state
      setClasses(prevClasses => 
        prevClasses.map(cls => 
          cls.id === editingClass.id 
            ? { ...cls, ...updatedClass } 
            : cls
        )
      );
      
      closeAllModals();
      showNotification('Class updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating class:', error);
      showNotification(`Failed to update class: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete confirmation
  const confirmDelete = (cls) => {
    setClassToDelete(cls);
    setShowDeleteConfirm(true);
  };

  // Handle actual delete
  const handleDelete = async () => {
    if (!classToDelete) return;
    
    setIsSaving(true);
    try {
      await deleteClass(classToDelete.id);
      
      // Update local state
      setClasses(prevClasses => 
        prevClasses.filter(cls => cls.id !== classToDelete.id)
      );
      
      closeAllModals();
      showNotification('Class deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting class:', error);
      showNotification(`Failed to delete class: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Format cell value based on column type
  const formatCellValue = (value, columnName, row) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    // Special handling for class_teacher - show teacher name instead of ID
    if (columnName === 'class_teacher' && row?.class_teacher_name) {
      return row.class_teacher_name;
    }
    
    // Format dates
    if (columnName.includes('date') || columnName.includes('created') || columnName.includes('updated')) {
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) {
        return value;
      }
    }
    
    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) {
      return (
        <div className="pagination">
          <div className="pagination-info">
            {filteredClasses.length > 0 
              ? `Showing ${filteredClasses.length} records` 
              : 'No records to display'}
          </div>
        </div>
      );
    }
    
    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        <button 
          onClick={() => paginate(1)} 
          disabled={currentPage === 1}
          className="page-nav"
          aria-label="First page"
        >
          &laquo;
        </button>
        <button 
          onClick={() => paginate(currentPage - 1)} 
          disabled={currentPage === 1}
          className="page-nav"
          aria-label="Previous page"
        >
          &lsaquo;
        </button>
        
        {startPage > 1 && (
          <span className="page-ellipsis">...</span>
        )}
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`page-number ${currentPage === number ? 'active' : ''}`}
            aria-label={`Page ${number}`}
          >
            {number}
          </button>
        ))}
        
        {endPage < totalPages && (
          <span className="page-ellipsis">...</span>
        )}
        
        <button 
          onClick={() => paginate(currentPage + 1)} 
          disabled={currentPage === totalPages}
          className="page-nav"
          aria-label="Next page"
        >
          &rsaquo;
        </button>
        <button 
          onClick={() => paginate(totalPages)} 
          disabled={currentPage === totalPages}
          className="page-nav"
          aria-label="Last page"
        >
          &raquo;
        </button>
        
        <div className="pagination-info">
          Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, filteredClasses.length)} of {filteredClasses.length} records
        </div>
      </div>
    );
  };

  return (
    <div className="classes-container">
      <header className="classes-header">
        <div className="header-center">
          <h2>Classes Management</h2>
        </div>
        <div className="class-summary">
          <div className="class-summary-item">
            <span className="summary-label">Total Classes:</span>
            <span className="summary-value">{filteredClasses.length}</span>
          </div>
        </div>
      </header>

      <div className="classes-controls">
        <div className="search-container">
          <div className="class-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={`Search ${searchColumn === 'all' ? 'all columns' : formatColumnName(searchColumn)}...`}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="column-dropdown">
            <button 
              className="column-selector"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              aria-label="Select search column"
            >
              <i className="fas fa-columns"></i>
              <span>{columnOptions.find(col => col.value === searchColumn)?.label || 'All Columns'}</span>
              <i className={`fas fa-chevron-${showColumnDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showColumnDropdown && (
              <div className="dropdown-menu">
                {columnOptions.map(option => (
                  <div
                    key={option.value}
                    className={`dropdown-item ${searchColumn === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setSearchColumn(option.value);
                      setShowColumnDropdown(false);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="button-group">
          <div className="export-container" style={{ position: 'relative' }}>
            <button 
              className="export-btn" 
              onClick={() => setShowExportOptions(!showExportOptions)}
              disabled={filteredClasses.length === 0}
            >
              <i className="fas fa-file-export"></i> Export
            </button>
            {showExportOptions && (
              <div className="export-options" onClick={e => e.stopPropagation()}>
                <div 
                  className="export-option"
                  onClick={() => {
                    handleExport(true);
                    setShowExportOptions(false);
                  }}
                >
                  Export All Records
                </div>
                <div 
                  className="export-option"
                  onClick={() => {
                    handleExport(false);
                    setShowExportOptions(false);
                  }}
                >
                  Export Current Page
                </div>
              </div>
            )}
          </div>
          <button 
            className="add-class-btn"
            onClick={() => {
              closeAllModals();
              setShowClassForm(true);
            }}
          >
            <i className="fas fa-plus"></i> Add New Class
          </button>
        </div>
      </div>

      {/* Loading and error states */}
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading classes data...</p>
        </div>
      )}
      
      {error && (
        <div className="error-container">
          <p><i className="fas fa-exclamation-circle"></i> {error}</p>
          <button onClick={fetchClassesData} className="retry-btn">
            <i className="fas fa-sync"></i> Retry
          </button>
        </div>
      )}

      {/* Classes Table */}
      {!isLoading && !error && (
        <div className="classes-table-container">
          <div className="table-scroll-wrapper">
            <table className="classes-table">
              <colgroup>
                <col style={{ width: '100px' }} /> {/* Actions column */}
                <col style={{ width: '120px' }} /> {/* Student count column */}
                {columns.map((col) => (
                  <col key={`col-${col}`} style={{ minWidth: '150px' }} />
                ))}
              </colgroup>
              <thead>
                <tr className="table-header">
                  <th className="actions-col">Actions</th>
                  <th
                    className={sortField === 'count' ? 'sort-active' : ''}
                    onClick={() => handleSort('count')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Count
                    {sortField === 'count' && (
                      <span className="sort-icon">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col}
                      className={`${sortField === col ? 'sort-active' : ''}`}
                      onClick={() => handleSort(col)}
                    >
                      {formatColumnName(col)}
                      {sortField === col && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
              {currentRecords.length > 0 ? (
                currentRecords.map((cls) => (
                  <tr key={cls.id} onClick={() => handleEdit(cls)}>
                    <td className="actions-col">
                      <div className="action-buttons">
                        <button
                          className="edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(cls);
                          }}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(cls);
                          }}
                          title="Delete"
                          disabled={isSaving}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                    <td className="student-count-cell">
                      {studentCounts[cls.class_name] ? (
                        <div>
                          <div className="active-count" title="Active Students">
                            {'Active: '}
                            {studentCounts[cls.class_name].active || 0}
                          </div>
                          <div className="inactive-count" title="Inactive Students">
                            {'Inactive: '}
                            {studentCounts[cls.class_name].inactive || 0}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="active-count">Active: 0</div>
                          <div className="inactive-count">Inactive: 0</div>
                        </div>
                      )}
                    </td>
                    {columns.map((col) => (
                      <td key={`${cls.id}-${col}`} title={formatCellValue(cls[col], col, cls)}>
                        <span className="cell-content">
                          {formatCellValue(cls[col], col, cls)}
                        </span>
                      </td>
                    ))}
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length + 2} className="no-records">
                      <div className="no-records-content">
                        <i className="fas fa-search" style={{ fontSize: '1.5rem' }}></i>
                        <p>No classes found matching your search criteria.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {renderPagination()}
        </div>
      )}
      
      {/* Notification */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}

      {/* Class Form Modal */}
      {showClassForm && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="classes-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingClass ? 'Edit Class' : 'Add New Class'}</h3>
              <button className="modal-close-btn" onClick={closeAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <ClassForm
                initialData={editingClass || { is_active: true }}
                onSave={editingClass ? handleUpdateClass : handleAddClass}
                onClose={closeAllModals}
                isEditMode={!!editingClass}
                isSaving={isSaving}
                columns={columns}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && classToDelete && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="classes-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="modal-close-btn" onClick={closeAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the class "{classToDelete.class_name}"?</p>
              <p className="text-danger">This action cannot be undone.</p>
              <div className="modal-actions">
                <button 
                  className="btn-cancel"
                  onClick={closeAllModals}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button 
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  {isSaving ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Classes;
